/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { isEqual } from 'lodash';
import { renderToStaticMarkup } from 'react-dom/server';
import {
	parse,
	stringify,
	CssTypes,
	type CssRuleAST,
	type CssAtRuleAST,
	type CssImportAST,
	type CssCommentAST,
	type CssDeclarationAST
} from '@adobe/css-tools';
import { CKEditor5Preview } from '../../components/CKEditor5Preview/CKEditor5Preview';
import CKEditor5PreviewCss from '../../components/CKEditor5Preview/CKEditor5Preview.css?raw';
import type { EditorOptions } from '../../components/SnippetsContainer/Snippets/types';

// Some CSS rules are defined to match elements added by the running editor. For the purposes of creating snippets,
// we do not have such dynamic elements in the preview component, so we must remove them from the CSS selectors.
// In other words, this is the fragment of a CSS selector that is omitted when querying elements in the preview component.
// In short, all ".ck" and ".ck-*" selector fragments are omitted.
const CSS_SELECTOR_TO_SKIP_FOR_CHECKING_MATCH = /\.ck\b.*$/;

// If the CSS rule targets the editor content (the ".ck-content" class), we must remove all the builder-specific selectors, that are defined
// before the ".ck-content" class. Otherwise, the styles will not be correctly applied to the document after exporting to PDF or to Word.
const CSS_SELECTOR_TO_TARGET_EDITOR_CONTENT = /.*(?=\.ck-content)/;

// After removing fragments from the CSS selectors, it may happen the selector ends with the combinator, so it becomes an invalid selector.
// These dangling combinators have to be removed.
const CSS_DANGLING_COMBINATOR = /\s*[ >+~]\s*$/;

// `stringify()` adds a blank line between a comment node and CSS path. We aim to remove it.
const REMOVE_BLANK_LINE_AFTER_COMMENT = /(\/\*[^*]*\*+([^/*][^*]*\*+)*\/\n)\n/gm;

// `stringify()` adds a blank line between import statements. We do not need it.
const REMOVE_BLANK_LINE_BETWEEN_FONT_IMPORT = /\n(\n@import)/gm;

export function getCodeCss( { editorOptions }: { editorOptions: EditorOptions } ): string {
	const cssAst = parse( CKEditor5PreviewCss );

	const previewComponent = convertPreviewComponentToDocument( editorOptions );

	cssAst.stylesheet.rules = cssAst.stylesheet.rules
		.filter( removeUnusedRulesAndComments( previewComponent ) )
		.filter( removeUnusedFonts )
		.map( removeUnusedVariablesAndComments )
		.map( prepareSelectorsForEditorContent )
		.reduce( mergeStyles, [] );

	return stringify( cssAst )
		.replaceAll( REMOVE_BLANK_LINE_BETWEEN_FONT_IMPORT, '$1' )
		.replaceAll( REMOVE_BLANK_LINE_AFTER_COMMENT, '$1' );
}

function convertPreviewComponentToDocument( editorOptions: EditorOptions ) {
	const { editorPlugins, editorType, toolbarOptions } = editorOptions;

	const staticMarkup = renderToStaticMarkup(
		<CKEditor5Preview
			locationOrigin=''
			editorPlugins={ editorPlugins }
			editorType={ editorType }
			toolbarOptions={ toolbarOptions }
		/>
	);

	const domParser = new DOMParser();

	return domParser.parseFromString( staticMarkup, 'text/html' );
}

function removeUnusedRulesAndComments( previewComponent: Document ) {
	return ( rule: CssAtRuleAST ) => {
		// Keep only comments that start with "!".
		if ( isCssComment( rule ) ) {
			if ( rule.comment.startsWith( '!' ) ) {
				rule.comment = rule.comment.substring( 1 );

				return true;
			}

			return false;
		}

		// Always allow all CSS instructions other than a "rule" (e.g. an "@import ..." instruction).
		if ( !isCssRule( rule ) ) {
			return true;
		}

		// For a CSS "rule" instruction, keep it only when:
		// - the parsed selector becomes an empty string, which means that the
		//   original selector targets only the dynamic elements that are omitted,
		// - the non-empty parsed selector still matches at least one element in the preview component.
		return rule.selectors
			.map( selector => selector
				.replace( CSS_SELECTOR_TO_SKIP_FOR_CHECKING_MATCH, '' )
				.replace( CSS_DANGLING_COMBINATOR, '' )
			)
			.some( selector => !selector || previewComponent.querySelector( selector ) );
	};
}

function removeUnusedFonts( rule: CssAtRuleAST, index: number, rules: Array<CssAtRuleAST> ) {
	if ( !isCssImport( rule ) ) {
		return true;
	}

	const fontImportUrl = rule.import.replace( /url\(['"](.*)['"]\)/, '$1' );

	// See https://developers.google.com/fonts/docs/css2#api_url_specification.
	const fonts = new URL( fontImportUrl ).searchParams
		.getAll( 'family' )
		.map( font => {
			const [ name ] = font.split( ':' );

			return name;
		} );

	// Keep all non-font imports.
	if ( !fonts.length ) {
		return true;
	}

	return fonts.some( font => isFontUsed( font, rules ) );
}

function removeUnusedVariablesAndComments( rule: CssAtRuleAST, index: number, rules: Array<CssAtRuleAST> ) {
	// Variables can be defined only in a "rule".
	if ( !isCssRule( rule ) ) {
		return rule;
	}

	rule.declarations = rule.declarations
		.filter( isCssDeclaration )
		.filter( declaration => {
			if ( !isVariable( declaration ) ) {
				return true;
			}

			return isVariableUsed( declaration.property, rules );
		} );

	return rule;
}

function prepareSelectorsForEditorContent( rule: CssAtRuleAST ) {
	// Selectors can be defined only in a "rule".
	if ( !isCssRule( rule ) ) {
		return rule;
	}

	rule.selectors = rule.selectors.map( selector => {
		return selector.replace( CSS_SELECTOR_TO_TARGET_EDITOR_CONTENT, '' );
	} );

	return rule;
}

function mergeStyles( result: Array<CssAtRuleAST>, rule: CssAtRuleAST ) {
	// Selectors can be defined only in a "rule".
	if ( !isCssRule( rule ) ) {
		result.push( rule );

		return result;
	}

	const sameSelectorsRule = result
		.filter( isCssRule )
		.find( existingRule => isEqual( existingRule.selectors, rule.selectors ) );

	if ( sameSelectorsRule ) {
		sameSelectorsRule.declarations.push( ...rule.declarations );

		sameSelectorsRule.declarations = sameSelectorsRule.declarations
			.filter( isCssDeclaration )
			.reduce( mergeDeclarations, [] );
	} else {
		result.push( rule );
	}

	return result;
}

function mergeDeclarations( result: Array<CssDeclarationAST>, declaration: CssDeclarationAST ) {
	const samePropertyDeclaration = result.find( existingDeclaration => existingDeclaration.property === declaration.property );

	if ( samePropertyDeclaration ) {
		samePropertyDeclaration.value = declaration.value;
	} else {
		result.push( declaration );
	}

	return result;
}

function isVariable( declaration: CssDeclarationAST ) {
	return declaration.property.startsWith( '--' );
}

function isVariableUsed( variable: string, rules: Array<CssAtRuleAST> ) {
	return rules
		.filter( isCssRule )
		.some( rule => isVariableUsedInRule( variable, rule ) );
}

function isVariableUsedInRule( variable: string, rule: CssRuleAST ) {
	return rule.declarations
		.filter( isCssDeclaration )
		.some( declaration => {
			const variableRegexp = new RegExp( `var\\(\\s*${ variable }\\s*\\)` );

			return variableRegexp.test( declaration.value );
		} );
}

function isFontUsed( font: string, rules: Array<CssAtRuleAST> ) {
	return rules
		.filter( isCssRule )
		.some( rule => isFontUsedInRule( font, rule ) );
}

function isFontUsedInRule( font: string, rule: CssRuleAST ) {
	return rule.declarations
		.filter( isCssDeclaration )
		.some( declaration => {
			if ( declaration.property === 'font' || declaration.property === 'font-family' ) {
				return declaration.value.includes( font );
			}
		} );
}

function isCssRule( item: CssAtRuleAST ): item is CssRuleAST {
	return item.type === CssTypes.rule;
}

function isCssComment( item: CssAtRuleAST ): item is CssCommentAST {
	return item.type === CssTypes.comment;
}

function isCssDeclaration( item: CssDeclarationAST | CssCommentAST ): item is CssDeclarationAST {
	return item.type === CssTypes.declaration;
}

function isCssImport( item: CssAtRuleAST ): item is CssImportAST {
	return item.type === CssTypes.import;
}
